import { Component, Inject, OnDestroy, OnInit, Optional } from '@angular/core';
import {
  ActionSheetController,
  AlertController,
  ModalController,
  NavController,
  ToastController,
} from '@ionic/angular';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { LanguageSwitcherPopupComponent } from '../language-switcher/language-switcher-popup.component';
import { LanguageService } from '../services/language.service';
import { ToggleService } from '../services/toggle.service';
import { UserService } from '../services/user.service';
import { AccountService } from '../services/account.service';
import { TranslateService } from '@ngx-translate/core';

type AccountUserType = 'admin' | 'basic' | 'readonly';

@Component({
  selector: 'app-account-settings-user-settings',
  templateUrl: './user-settings.component.html',
  styleUrls: ['./user-settings.component.scss'],
})
export class AccountSettingsUserSettingsComponent implements OnInit, OnDestroy {
  public email = '';
  public currentLanguageInfo: any = { name: 'English', flag: '🇺🇸' };
  private languageSubscription?: Subscription;
  private storageSubscription?: Subscription;

  constructor(
    private navCtrl: NavController,
    private modalController: ModalController,
    private router: Router,
    private languageService: LanguageService,
    private toggleService: ToggleService,
    private userService: UserService,
    private accountService: AccountService,
    private translate: TranslateService,
    private alertController: AlertController,
    private actionSheetController: ActionSheetController,
    private toastController: ToastController,
    @Optional() @Inject('accountInformation') public accountInformation: any,
    @Optional() @Inject('onAdminAccountSwitch') private onAdminAccountSwitch?: (accountId: number) => Promise<void>
  ) {}

  ngOnInit() {
    const loginData = this.userService.getLoginData();
    this.email = loginData?.email || '';
    if (!this.email) {
      this.userService.getStoredEmail();
      this.storageSubscription = this.userService.storageUpdate.subscribe((data) => {
        if (data?.email) {
          this.email = data.email;
        }
      });
    }

    this.currentLanguageInfo = this.languageService.getCurrentLanguageInfo();
    this.languageSubscription = this.languageService.currentLanguage$.subscribe(() => {
      this.currentLanguageInfo = this.languageService.getCurrentLanguageInfo();
    });
  }

  ngOnDestroy() {
    if (this.languageSubscription) {
      this.languageSubscription.unsubscribe();
    }
    if (this.storageSubscription) {
      this.storageSubscription.unsubscribe();
    }
  }

  isToggleEnabled(toggleName: string): boolean {
    return this.toggleService.isToggleEnabled(toggleName);
  }

  /** KilnLink premium account (alerts / device & notification settings are premium-only). */
  isPremiumAccount(): boolean {
    return this.userService.isPremium();
  }

  currentAccountLabel(): string {
    return this.accountInformation?.company?.name
      || this.accountInformation?.general?.external_id
      || '';
  }

  currentAccountUserType(): AccountUserType | null {
    return this.userService.getAccountUserType();
  }

  currentAccountPermissionLabel(): string {
    const type = this.currentAccountUserType();
    if (!type) {
      return '';
    }
    const keyByType: Record<AccountUserType, string> = {
      admin: 'USERS.ADMINISTRATOR',
      basic: 'USERS.BASIC_USER',
      readonly: 'USERS.READONLY_USER',
    };
    return this.translate.instant(keyByType[type]);
  }

  async presentRoleInfo(event: Event): Promise<void> {
    event.stopPropagation();
    const type = this.currentAccountUserType();
    if (!type) {
      return;
    }

    const messageKeyByType: Record<AccountUserType, string> = {
      admin: 'USER_SETTINGS.ROLE_INFO_ADMIN',
      basic: 'USER_SETTINGS.ROLE_INFO_BASIC',
      readonly: 'USER_SETTINGS.ROLE_INFO_READONLY',
    };

    const alert = await this.alertController.create({
      header: this.currentAccountPermissionLabel(),
      message: this.translate.instant(messageKeyByType[type]),
      buttons: [this.translate.instant('SWITCH_ACCOUNTS.CLOSE')],
    });
    await alert.present();
  }

  async openAccountOptions(): Promise<void> {
    const buttons: Array<{ text: string; role?: string; handler?: () => void }> = [];
    const canSwitchAccounts = !this.userService.isLoggedInAsAnotherUser();

    if (canSwitchAccounts) {
      const accounts = await this.userService.getAdminAccounts();
      const currentId = parseInt(String(this.accountService.getCurrentAccount()), 10);
      accounts
        .filter((acct) => acct.id !== currentId)
        .forEach((acct) => {
          buttons.push({
            text: acct.name,
            handler: () => {
              void this.switchToAccount(acct.id);
            },
          });
        });
    }

    buttons.push({
      text: this.translate.instant('USER_SETTINGS.CREATE_ACCOUNT'),
      handler: () => {
        this.goToCreateAccount();
      },
    });
    buttons.push({
      text: this.translate.instant('MANAGE_NETWORK.CANCEL'),
      role: 'cancel',
    });

    const sheet = await this.actionSheetController.create({
      header: this.translate.instant('USER_SETTINGS.ACCOUNT_OPTIONS'),
      buttons,
    });
    await sheet.present();
  }

  private async switchToAccount(accountId: number): Promise<void> {
    console.log('switchToAccount', accountId);
    if (this.onAdminAccountSwitch) {
      await this.onAdminAccountSwitch(accountId);
      return;
    }

    try {
      await this.accountService.view(accountId);
      try {
        await this.userService.updateLoginData();
      } catch {
        /* navigation below still refreshes account admin */
      }
      await this.router.navigate(['/account-admin'], { queryParams: { tab: 'user-settings' } });
    } catch {
      const toast = await this.toastController.create({
        message: this.translate.instant('ACCOUNT_ADMIN.SWITCH_ACCOUNT_FAILURE'),
        duration: 4000,
        position: 'bottom',
        color: 'danger',
      });
      await toast.present();
    }
  }

  goToCreateAccount(): void {
    this.navCtrl.navigateForward('/create-account');
  }

  async openLanguageSwitcher() {
    if (!this.isToggleEnabled('internationalization')) {
      return;
    }
    const modal = await this.modalController.create({
      component: LanguageSwitcherPopupComponent,
      componentProps: {},
      cssClass: 'language-switcher-modal',
    });
    return await modal.present();
  }

  goToChangePassword() {
    this.navCtrl.navigateForward('/change-password');
  }

  goToSwitchAccounts() {
    this.navCtrl.navigateForward('/switch-account');
  }

  goToAlertSettings() {
    this.router.navigate(['/account-admin'], { queryParams: { tab: 'alert-settings' } });
  }

  goToDeviceSettings() {
    this.router.navigate(['/account-admin'], { queryParams: { tab: 'users', section: 'notifications' } });
  }
}
